import {apiFetch} from "../client";

// Orders domain (admin)

export type OrderStatus = "new" | "pending" | "success" | "cancelled" | "appeal" | "partially_filled";
export type OrderSide = "in" | "out";

export interface OrderItem {
    externalCompletion?: import("../../generated/openapi-contract").ExternalCompletion;
    orderId: string;
    remoteId: string;
    merchantName: string;
    status: OrderStatus;
    side: OrderSide;
    amount: number;
    amountRemaining: number;
    paymentsMade: number;
    maxPayments: number;
    currency: string;
    currencyScale: number;
    createdAt: string;
    updatedAt: string;
    appeal: boolean;
    // Заявка кабинета, которую уже нельзя провести, и трейдер её не брал: спора
    // по ней нет и быть не может, а из пула она ушла — эта метка единственное
    // место, где админ её вообще увидит.
    externalStalled: boolean;
    /**
     * USDT-этап заявки-обмена (`pending`, `confirmed`, `escalated`); отсутствует
     * у любой заявки без второй ноги. Отвечает на единственный вопрос, который
     * у админа по такой заявке бывает: закрыт ли этап и не нужно ли закрыть его
     * руками за пропавшего трейдера.
     */
    exchangePayoutStatus?: string;
}

export interface ListOrdersParams {
    remoteId?: string;
    merchantId?: string;
    merchantName?: string;
    status?: OrderStatus;
    side?: OrderSide;
    from?: string;
    to?: string;
    traderId?: string;
    teamId?: string;
    minAmount?: number;
    maxAmount?: number;
    limit?: number;
    offset?: number;
}

export interface OrdersResponse {
    items: OrderItem[];
    limit: number;
    offset: number;
    hasMore: boolean;
    nextOffset?: number | null;
}

export interface OrderDetailsResponse {
    id: string;
    remoteId: string;
    bank: string;
    amount: number;
    clientCard: string | null;
    clientPhone: string | null;
    clientBank: string | null;
    clientFio: string;
    postback: string | null;
    sub1: string | null;
    sub2: string | null;
    sub3: string | null;
    receiptUrl: string | null;
    status: string;
    createdAt: string;
    updatedAt: string;
}

export function listOrders(token: string | null, query: ListOrdersParams = {}): Promise<OrdersResponse> {
    const params = new URLSearchParams();
    if (query.remoteId) params.set("remoteId", query.remoteId);
    if (query.merchantId) params.set("merchantId", query.merchantId);
    if (query.merchantName) params.set("merchantName", query.merchantName);
    if (query.status) params.set("status", query.status);
    if (query.side) params.set("side", query.side);
    if (query.from) params.set("from", query.from);
    if (query.to) params.set("to", query.to);
    if (query.traderId) params.set("traderId", query.traderId);
    if (query.teamId) params.set("teamId", query.teamId);
    if (query.minAmount != null) params.set("minAmount", String(query.minAmount));
    if (query.maxAmount != null) params.set("maxAmount", String(query.maxAmount));
    if (query.limit != null) params.set("limit", String(query.limit));
    if (query.offset != null) params.set("offset", String(query.offset));

    const suffix = params.toString();
    return apiFetch<OrdersResponse>(`/orders${suffix ? `?${suffix}` : ""}`, token);
}

export function getOrder(token: string | null, orderId: string): Promise<OrderDetailsResponse> {
    return apiFetch<OrderDetailsResponse>(`/order/${encodeURIComponent(orderId)}`, token);
}

export interface CancelStalledOrderResponse {
    orderId: string;
    status: OrderStatus;
}

// Отмена вставшей заявки кабинета — единственное действие, доступное админу по
// такой заявке: спора по ней нет и быть не может (спор живёт на платеже), а из
// пула она ушла. Наружу, на площадку, при этом не уходит ничего.
export function cancelStalledOrder(token: string | null, orderId: string): Promise<CancelStalledOrderResponse> {
    return apiFetch<CancelStalledOrderResponse>(`/orders/${encodeURIComponent(orderId)}/cancel`, token, {
        method: "POST",
    });
}

export interface CloseExchangePayoutRequest {
    /** Hash транзакции, если админ его знает; null — не знает. */
    txHash: string | null;
    /** Зачем этап закрывается чужими руками. Обязательна. */
    reason: string;
}

export interface CloseExchangePayoutResponse {
    orderId: string;
    payoutStatus: string;
}

/**
 * POST /v1/orders/{orderId}/exchange/payout-close — админ закрывает USDT-этап
 * за пропавшего трейдера.
 *
 * Денег не двигает: рубли по заявке проведены раньше, а перевод USDT идёт между
 * кошельком трейдера и кошельком клиента, мимо нашей книги. Записывается факт
 * закрытия, причина в журнале и толчок постбека мерчанту.
 */
export function closeExchangePayout(
    token: string | null,
    orderId: string,
    body: CloseExchangePayoutRequest,
): Promise<CloseExchangePayoutResponse> {
    return apiFetch<CloseExchangePayoutResponse>(
        `/orders/${encodeURIComponent(orderId)}/exchange/payout-close`,
        token,
        {method: "POST", body: JSON.stringify(body)},
    );
}

/** Одна находка наблюдателя цепочки на адресе клиента. */
export interface ExchangePayoutObservedItem {
    txId: string;
    eventIndex: number;
    network: string;
    fromAddress: string;
    /** Готовая строка USDT с шестью знаками: деньги форматирует сервер. */
    amount: string;
    blockTime: string;
    receiptOk: boolean;
    /** `matched`, `amount_mismatch`, `before_window`, `after_window`, `duplicate`. */
    verdict: string;
    observedAt: string;
}

export interface ExchangePayoutObservedResponse {
    orderId: string;
    items: ExchangePayoutObservedItem[];
}

/**
 * GET /v1/orders/{orderId}/exchange/observed — журнал находок наблюдателя.
 *
 * Read-only и по заявке, а не по адресу: адрес чужой, и выдача по нему означала
 * бы возможность спросить «что приходило на этот кошелёк» по произвольной
 * строке. Нужен, чтобы разбирать незасчитанный перевод в админке, а не
 * запросом в базу.
 */
export function listExchangePayoutObserved(
    token: string | null,
    orderId: string,
): Promise<ExchangePayoutObservedResponse> {
    return apiFetch<ExchangePayoutObservedResponse>(
        `/orders/${encodeURIComponent(orderId)}/exchange/observed`,
        token,
    );
}
